import { useEffect } from 'react';
import { supabase } from '../supabase';
import { withTimeout } from '../utils/async/withTimeout';
import { subscribeAppResume } from '../utils/ui/appResumeGate';

const READ_TIMEOUT_MS = 8000;
/** Min gap between foreground re-reads (the gate already needs 60s away; this is a second belt). */
export const LOCALE_RESUME_MIN_GAP_MS = 60 * 1000;

/**
 * LIVE SYNC of the signed-in user's language across devices (KAN-304). Renders nothing.
 * One re-read of the OWN row (`profiles.id` / `platform_users.user_id`) per return to the foreground via
 * `subscribeAppResume` (never its own listener), throttled to 60s. Call budget: <=1 read per resume, so
 * <=60/hour worst case per active user (far lower in practice: the gate needs 60s away).
 * A changed value is applied through `applyLocale` (the cross-fade). NULL / error / timeout = no change, and a
 * read is dropped if the user changed the language in this tab while it was out.
 */
export default function useLocaleResumeSync({
  userId, isSuperAdmin, enabled, targetLocaleRef, localChangeCountRef, applyLocale,
}) {
  useEffect(() => {
    if (!userId || !enabled) return undefined;
    let cancelled = false;
    let lastReadAt = Date.now(); // the sign-in read just happened
    const read = async () => {
      const now = Date.now();
      if (now - lastReadAt < LOCALE_RESUME_MIN_GAP_MS) return;
      lastReadAt = now;
      const startedAt = localChangeCountRef.current;
      try {
        const query = isSuperAdmin
          ? supabase.from('platform_users').select('preferred_locale').eq('user_id', userId).maybeSingle()
          : supabase.from('profiles').select('preferred_locale').eq('id', userId).maybeSingle();
        const { data, error } = await withTimeout(query, READ_TIMEOUT_MS);
        if (cancelled || error || localChangeCountRef.current !== startedAt) return;
        const raw = String(data?.preferred_locale || '').trim().toLowerCase();
        if (raw !== 'en' && raw !== 'es') return;
        if (raw !== targetLocaleRef.current) await applyLocale(raw);
      } catch { /* keep the current language */ }
    };
    const unsubscribe = subscribeAppResume(() => { read(); });
    return () => { cancelled = true; unsubscribe(); };
  }, [userId, isSuperAdmin, enabled, targetLocaleRef, localChangeCountRef, applyLocale]);
  return null;
}
